import { subject } from '@casl/ability';
import { ActionIcon, Group, Popover, SegmentedControl } from '@mantine/core';
import { IconShare2 } from '@tabler/icons-react';
import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
} from 'react';
import {
    explorerActions,
    selectColumnOrder,
    selectIsEditMode,
    selectIsResultsExpanded,
    selectMetricQuery,
    selectSavedChart,
    selectSorts,
    selectTableName,
    useExplorerDispatch,
    useExplorerSelector,
} from '../../../features/explorer/store';
import { uploadGsheet } from '../../../hooks/gdrive/useGdrive';
import { useExplorerQuery } from '../../../hooks/useExplorerQuery';
import { useProjectUuid } from '../../../hooks/useProjectUuid';
import { Can } from '../../../providers/Ability';
import useApp from '../../../providers/App/useApp';
import { ExplorerSection } from '../../../providers/Explorer/types';
import AddTableCalculationButton from '../../AddTableCalculationButton';
import CollapsableCard from '../../common/CollapsableCard/CollapsableCard';
import {
    COLLAPSABLE_CARD_ACTION_ICON_PROPS,
    COLLAPSABLE_CARD_POPOVER_PROPS,
} from '../../common/CollapsableCard/constants';
import MantineIcon from '../../common/MantineIcon';
import ExportSelector from '../../ExportSelector';
import SortButton from '../../SortButton';
import { ExplorerResults } from './ExplorerResults';
import { ResultsViewMode } from './types';
import { useGroupedResultsAvailability } from './useGroupedResultsAvailability';

const ResultsCard: FC = memo(() => {
    const projectUuid = useProjectUuid();

    // View mode state for switching between results and grouped results
    const [viewMode, setViewMode] = useState<ResultsViewMode>(
        ResultsViewMode.RESULTS,
    );

    const isEditMode = useExplorerSelector(selectIsEditMode);
    const resultsIsOpen = useExplorerSelector(selectIsResultsExpanded);
    const dispatch = useExplorerDispatch();
    const tableName = useExplorerSelector(selectTableName);
    const sorts = useExplorerSelector(selectSorts);
    const metricQuery = useExplorerSelector(selectMetricQuery);
    const columnOrder = useExplorerSelector(selectColumnOrder);

    // Check if grouped view is available
    const { isSqlPivotEnabled, isGroupedDisabled } =
        useGroupedResultsAvailability();
    const isGroupedView =
        viewMode === ResultsViewMode.GROUPED && !isGroupedDisabled;

    // Reset to results view when grouped becomes unavailable
    useEffect(() => {
        if (isGroupedDisabled && viewMode === ResultsViewMode.GROUPED) {
            setViewMode(ResultsViewMode.RESULTS);
        }
    }, [isGroupedDisabled, viewMode]);

    const { queryResults, getDownloadQueryUuid } = useExplorerQuery();

    const totalResults = queryResults.totalResults;

    const savedChart = useExplorerSelector(selectSavedChart);

    const toggleExpandedSection = useCallback(
        (section: ExplorerSection) => {
            dispatch(explorerActions.toggleExpandedSection(section));
        },
        [dispatch],
    );

    const disabled = useMemo(() => (totalResults ?? 0) <= 0, [totalResults]);

    const toggleCard = useCallback(
        () => toggleExpandedSection(ExplorerSection.RESULTS),
        [toggleExpandedSection],
    );
    const { user } = useApp();

    const getGsheetLink = async () => {
        if (projectUuid) {
            return uploadGsheet({
                projectUuid,
                exploreId: tableName,
                metricQuery,
                columnOrder,
                showTableNames: true,
                // No pivotConfig - ResultsCard only shows raw table data
            });
        } else {
            throw new Error('Project UUID is missing');
        }
    };

    // ResultsCard always downloads raw unpivoted results
    const getResultsCardDownloadQueryUuid = useCallback(
        (limit: number | null) => {
            return getDownloadQueryUuid(limit, false);
        },
        [getDownloadQueryUuid],
    );

    return (
        <CollapsableCard
            title="Results"
            isOpen={resultsIsOpen}
            onToggle={toggleCard}
            disabled={!tableName}
            headerElement={
                // Hide header controls when in grouped view
                isGroupedView ? null : (
                    <Group noWrap spacing="xs">
                        {tableName && sorts.length > 0 && (
                            <SortButton isEditMode={isEditMode} sorts={sorts} />
                        )}
                    </Group>
                )
            }
            rightHeaderElement={
                projectUuid &&
                resultsIsOpen &&
                tableName && (
                    <Group spacing="xs" noWrap>
                        {isSqlPivotEnabled && !isGroupedDisabled && (
                            <SegmentedControl
                                size="xs"
                                data={[
                                    {
                                        label: 'Results',
                                        value: ResultsViewMode.RESULTS,
                                    },
                                    {
                                        label: 'Chart results',
                                        value: ResultsViewMode.GROUPED,
                                    },
                                ]}
                                value={viewMode}
                                onChange={(value) =>
                                    setViewMode(value as ResultsViewMode)
                                }
                            />
                        )}

                        {/* Hide AddColumnButton when in grouped view */}
                        {!isGroupedView && (
                            <Can
                                I="manage"
                                this={subject('Explore', {
                                    organizationUuid:
                                        user.data?.organizationUuid,
                                    projectUuid,
                                })}
                            >
                                {isEditMode && <AddTableCalculationButton />}
                            </Can>
                        )}

                        <Can
                            I="manage"
                            this={subject('ExportCsv', {
                                organizationUuid: user.data?.organizationUuid,
                                projectUuid,
                            })}
                        >
                            <Popover
                                {...COLLAPSABLE_CARD_POPOVER_PROPS}
                                disabled={disabled}
                                position="bottom-end"
                            >
                                <Popover.Target>
                                    <ActionIcon
                                        data-testid="export-csv-button"
                                        {...COLLAPSABLE_CARD_ACTION_ICON_PROPS}
                                        disabled={disabled}
                                    >
                                        <MantineIcon icon={IconShare2} />
                                    </ActionIcon>
                                </Popover.Target>

                                <Popover.Dropdown>
                                    <ExportSelector
                                        projectUuid={projectUuid}
                                        totalResults={totalResults}
                                        getDownloadQueryUuid={
                                            getResultsCardDownloadQueryUuid
                                        }
                                        getGsheetLink={getGsheetLink}
                                        columnOrder={columnOrder}
                                        customLabels={undefined} // for results table download, don't override labels
                                        hiddenFields={undefined} // for results table download, don't hide columns
                                        chartName={savedChart?.name}
                                        showTableNames
                                    />
                                </Popover.Dropdown>
                            </Popover>
                        </Can>
                    </Group>
                )
            }
        >
            <ExplorerResults viewMode={viewMode} />
        </CollapsableCard>
    );
});

export default ResultsCard;
